/**
 * jobService — real Supabase implementation.
 *
 * Public visibility model:
 *   • Anonymous + authenticated visitors see ONLY status = 'active' jobs.
 *     This is enforced by RLS (`jobs_public_read`); we still pass it
 *     explicitly so the SQL is self-documenting and works under any
 *     future relaxation.
 *   • Admins (recruiter / compliance_admin / admin / super_admin) can see
 *     every status — drafts, archived, paused. RLS handles that path too.
 *
 * The service returns the same `Job` shape regardless of caller — the
 * embedded Hospital is hydrated via a join, so the existing `j.hospital.name`
 * style references in pages keep working.
 */
import { getSupabase, isSupabaseConfigured } from '@/services/supabase';
import { mockJobs } from '@/mock/jobs';
import { mockHospitals } from '@/mock/hospitals';
import type {
  AuStateCode,
  Hospital,
  Job,
  JobStatus,
  JobType,
  RateType,
  SeniorityCode,
  Specialty,
} from '@/types';

const useMockAuth = (import.meta.env.VITE_USE_MOCK_AUTH ?? 'true') === 'true';
const isMock = () => useMockAuth || !isSupabaseConfigured;

const delay = (ms = 250) => new Promise((r) => setTimeout(r, ms));

export interface JobSearchFilters {
  q?: string;
  state?: string | null;
  specialty?: string | null;
  jobType?: string | null;
  status?: JobStatus | null;
  /** Optional minimum hourly-equivalent rate filter (used by saved searches). */
  minRate?: number;
  sort?: 'newest' | 'rate_desc' | 'rate_asc' | 'closing_soon';
}

// ─── Row → Domain mappers ────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToHospital(row: any): Hospital {
  return {
    id: row.id,
    name: row.name,
    shortName: row.short_name ?? null,
    city: row.city,
    state: row.state as AuStateCode,
    postcode: row.postcode ?? null,
    contactName: row.contact_name ?? null,
    contactEmail: row.contact_email ?? null,
    contactPhone: row.contact_phone ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToJob(row: any): Job {
  return {
    id: row.id,
    hospitalId: row.hospital_id,
    hospital: row.hospital ? rowToHospital(row.hospital) : ({} as Hospital),
    title: row.title,
    specialty: row.specialty as Specialty,
    seniorityRequired: (row.seniority_required ?? []) as SeniorityCode[],
    city: row.city,
    state: row.state as AuStateCode,
    postcode: row.postcode ?? null,
    rateAmount: Number(row.rate_amount),
    rateType: row.rate_type as RateType,
    startDate: row.start_date,
    endDate: row.end_date ?? null,
    jobType: row.job_type as JobType,
    description: row.description,
    requirements: row.requirements ?? [],
    benefits: row.benefits ?? null,
    accommodationProvided: row.accommodation_provided,
    travelProvided: row.travel_provided,
    status: row.status as JobStatus,
    applicationsCount: row.applications_count ?? 0,
    viewsCount: row.views_count ?? 0,
    postedAt: row.posted_at ?? row.created_at,
    closedAt: row.closed_at ?? null,
    createdBy: row.created_by ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Service ─────────────────────────────────────────────────────────────

export const jobService = {
  /**
   * List jobs matching filters. RLS narrows the result set automatically:
   *   • Anon + authenticated → only status='active'
   *   • Admin → everything
   * Pass `status` explicitly to override (e.g. admin viewing drafts).
   */
  async list(filters: JobSearchFilters = {}): Promise<Job[]> {
    if (isMock()) {
      await delay();
      let result = [...mockJobs];
      if (filters.status) result = result.filter((j) => j.status === filters.status);
      if (filters.state) result = result.filter((j) => j.state === filters.state);
      if (filters.specialty) result = result.filter((j) => j.specialty === filters.specialty);
      if (filters.jobType) result = result.filter((j) => j.jobType === filters.jobType);
      if (filters.q) {
        const t = filters.q.toLowerCase();
        result = result.filter(
          (j) =>
            j.title.toLowerCase().includes(t) ||
            j.specialty.toLowerCase().includes(t) ||
            j.city.toLowerCase().includes(t) ||
            j.hospital.name.toLowerCase().includes(t),
        );
      }
      switch (filters.sort) {
        case 'rate_desc':
          result.sort((a, b) => b.rateAmount - a.rateAmount);
          break;
        case 'rate_asc':
          result.sort((a, b) => a.rateAmount - b.rateAmount);
          break;
        case 'closing_soon':
          result.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
          break;
        default:
          result.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
      }
      return result;
    }

    let q = getSupabase()
      .from('jobs')
      .select(`
        *,
        hospital:hospitals(*)
      `);

    if (filters.status) {
      q = q.eq('status', filters.status);
    }
    if (filters.state)     q = q.eq('state', filters.state);
    if (filters.specialty) q = q.eq('specialty', filters.specialty);
    if (filters.jobType)   q = q.eq('job_type', filters.jobType);
    if (filters.q) {
      // Title-only ilike for now. A trigram or full-text search index
      // would let us match across hospital.name + city too — wire later.
      q = q.ilike('title', `%${filters.q}%`);
    }

    switch (filters.sort) {
      case 'rate_desc':     q = q.order('rate_amount', { ascending: false }); break;
      case 'rate_asc':      q = q.order('rate_amount', { ascending: true }); break;
      case 'closing_soon':  q = q.order('start_date',  { ascending: true }); break;
      default:              q = q.order('posted_at',   { ascending: false, nullsFirst: false });
    }

    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(rowToJob);
  },

  async byId(id: string): Promise<Job | null> {
    if (isMock()) {
      await delay(120);
      return mockJobs.find((j) => j.id === id) ?? null;
    }
    const { data, error } = await getSupabase()
      .from('jobs')
      .select('*, hospital:hospitals(*)')
      .eq('id', id)
      .maybeSingle();
    if (error) {
      // PGRST116 = no row found via maybeSingle on RLS-filtered query (e.g.
      // a non-admin trying to view a draft). Treat as "not found".
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? rowToJob(data) : null;
  },

  /**
   * Aggregate counts used by admin dashboards. RLS limits draft visibility
   * to admins, so this is admin-flavoured by default — calling as a doctor
   * yields the active-only view.
   */
  async stats(): Promise<{ active: number; draft: number; totalApps: number; totalViews: number; avgRate: number }> {
    if (isMock()) {
      await delay();
      const active = mockJobs.filter((j) => j.status === 'active').length;
      const draft = mockJobs.filter((j) => j.status === 'draft').length;
      const totalApps = mockJobs.reduce((s, j) => s + j.applicationsCount, 0);
      const totalViews = mockJobs.reduce((s, j) => s + j.viewsCount, 0);
      const avgRate = Math.round(
        mockJobs.reduce((s, j) => s + j.rateAmount, 0) / Math.max(mockJobs.length, 1),
      );
      return { active, draft, totalApps, totalViews, avgRate };
    }

    const { data, error } = await getSupabase()
      .from('jobs')
      .select('status, rate_amount, applications_count, views_count');
    if (error) throw error;
    const rows = data ?? [];
    const active = rows.filter((r) => r.status === 'active').length;
    const draft = rows.filter((r) => r.status === 'draft').length;
    const totalApps = rows.reduce((s, r) => s + (r.applications_count ?? 0), 0);
    const totalViews = rows.reduce((s, r) => s + (r.views_count ?? 0), 0);
    const avgRate = rows.length
      ? Math.round(rows.reduce((s, r) => s + Number(r.rate_amount ?? 0), 0) / rows.length)
      : 0;
    return { active, draft, totalApps, totalViews, avgRate };
  },

  async byLocation(): Promise<Array<{ state: string; count: number }>> {
    if (isMock()) {
      await delay();
      const map = new Map<string, number>();
      for (const j of mockJobs) map.set(j.state, (map.get(j.state) ?? 0) + 1);
      return [...map.entries()]
        .map(([state, count]) => ({ state, count }))
        .sort((a, b) => b.count - a.count);
    }

    const { data, error } = await getSupabase()
      .from('jobs')
      .select('state')
      .eq('status', 'active');
    if (error) throw error;
    const map = new Map<string, number>();
    for (const r of data ?? []) {
      const s = (r as { state: string }).state;
      map.set(s, (map.get(s) ?? 0) + 1);
    }
    return [...map.entries()]
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count);
  },

  // ─── Admin write actions (admins only by RLS) ──────────────────────────

  /**
   * Create a new job. Admin-flavoured. RLS enforces public.is_admin().
   * Returns the newly-created Job (with hospital joined).
   */
  async create(input: {
    hospitalId: string;
    title: string;
    specialty: string;
    city: string;
    state: AuStateCode;
    rateAmount: number;
    rateType: RateType;
    jobType: JobType;
    startDate: string;
    endDate?: string | null;
    description: string;
    requirements?: string[];
    benefits?: string[];
    seniorityRequired?: SeniorityCode[];
    accommodationProvided?: boolean;
    travelProvided?: boolean;
    status?: JobStatus; // defaults to 'draft'
    createdBy?: string; // current user's profile.id
  }): Promise<Job> {
    if (isMock()) {
      await delay(300);
      // Cheap mock — the calling page rarely needs the new row to round-trip.
      const hospital = mockHospitals.find((h) => h.id === input.hospitalId) ?? mockHospitals[0]!;
      const now = new Date().toISOString();
      const newJob: Job = {
        id: `job_${crypto.randomUUID().slice(0, 8)}`,
        hospitalId: input.hospitalId,
        hospital,
        title: input.title,
        // Specialty is a string from the DB seed (specialties.code) — cast
        // to the Specialty union; the values are validated at the form level.
        specialty: input.specialty as Specialty,
        seniorityRequired: input.seniorityRequired ?? [],
        city: input.city,
        state: input.state,
        rateAmount: input.rateAmount,
        rateType: input.rateType,
        startDate: input.startDate,
        endDate: input.endDate ?? null,
        jobType: input.jobType,
        description: input.description,
        requirements: input.requirements ?? [],
        benefits: input.benefits ?? null,
        accommodationProvided: input.accommodationProvided ?? false,
        travelProvided: input.travelProvided ?? false,
        status: input.status ?? 'draft',
        applicationsCount: 0,
        viewsCount: 0,
        postedAt: input.status === 'active' ? now : now,
        createdBy: input.createdBy ?? '',
        createdAt: now,
        updatedAt: now,
      };
      mockJobs.unshift(newJob);
      return newJob;
    }

    const { data, error } = await getSupabase()
      .from('jobs')
      .insert({
        hospital_id: input.hospitalId,
        title: input.title,
        specialty: input.specialty,
        seniority_required: input.seniorityRequired ?? [],
        city: input.city,
        state: input.state,
        rate_amount: input.rateAmount,
        rate_type: input.rateType,
        start_date: input.startDate,
        end_date: input.endDate ?? null,
        job_type: input.jobType,
        description: input.description,
        requirements: input.requirements ?? [],
        benefits: input.benefits ?? [],
        accommodation_provided: input.accommodationProvided ?? false,
        travel_provided: input.travelProvided ?? false,
        status: input.status ?? 'draft',
        // posted_at gets set when status flips to active (see update). For
        // new jobs created directly as active, set it now.
        posted_at: input.status === 'active' ? new Date().toISOString() : null,
        created_by: input.createdBy ?? null,
      })
      .select('*, hospital:hospitals(*)')
      .single();
    if (error) throw error;
    return rowToJob(data);
  },

  /** Patch a job. Admin-only by RLS. Pass only the fields you're changing. */
  async update(id: string, patch: Partial<{
    title: string;
    specialty: string;
    seniorityRequired: SeniorityCode[];
    city: string;
    state: AuStateCode;
    postcode: string | null;
    rateAmount: number;
    rateType: RateType;
    startDate: string;
    endDate: string | null;
    jobType: JobType;
    description: string;
    requirements: string[];
    benefits: string[];
    accommodationProvided: boolean;
    travelProvided: boolean;
    status: JobStatus;
  }>): Promise<Job | null> {
    if (isMock()) {
      await delay(200);
      const j = mockJobs.find((x) => x.id === id);
      if (!j) return null;
      Object.assign(j, patch, { updatedAt: new Date().toISOString() });
      if (patch.status === 'active' && !j.postedAt) j.postedAt = new Date().toISOString();
      return j;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dbPatch: Record<string, any> = {};
    if (patch.title != null)                  dbPatch.title = patch.title;
    if (patch.specialty != null)              dbPatch.specialty = patch.specialty;
    if (patch.seniorityRequired != null)      dbPatch.seniority_required = patch.seniorityRequired;
    if (patch.city != null)                   dbPatch.city = patch.city;
    if (patch.state != null)                  dbPatch.state = patch.state;
    if (patch.postcode !== undefined)         dbPatch.postcode = patch.postcode;
    if (patch.rateAmount != null)             dbPatch.rate_amount = patch.rateAmount;
    if (patch.rateType != null)               dbPatch.rate_type = patch.rateType;
    if (patch.startDate != null)              dbPatch.start_date = patch.startDate;
    if (patch.endDate !== undefined)          dbPatch.end_date = patch.endDate;
    if (patch.jobType != null)                dbPatch.job_type = patch.jobType;
    if (patch.description != null)            dbPatch.description = patch.description;
    if (patch.requirements != null)           dbPatch.requirements = patch.requirements;
    if (patch.benefits != null)               dbPatch.benefits = patch.benefits;
    if (patch.accommodationProvided != null)  dbPatch.accommodation_provided = patch.accommodationProvided;
    if (patch.travelProvided != null)         dbPatch.travel_provided = patch.travelProvided;
    if (patch.status != null) {
      dbPatch.status = patch.status;
      // Stamp posted_at on first activation
      if (patch.status === 'active') dbPatch.posted_at = new Date().toISOString();
      if (patch.status === 'closed') {
        dbPatch.closed_at = new Date().toISOString();
      }
    }

    const { data, error } = await getSupabase()
      .from('jobs')
      .update(dbPatch)
      .eq('id', id)
      .select('*, hospital:hospitals(*)')
      .maybeSingle();
    if (error) throw error;
    return data ? rowToJob(data) : null;
  },

  /** Increment views_count atomically — fired from public job detail. */
  async incrementViews(jobId: string): Promise<void> {
    if (isMock()) return;
    // We can't atomically increment via the REST API without an RPC, so
    // for now we read+write. Race conditions are acceptable here (it's
    // a public counter — exact accuracy isn't critical).
    const supa = getSupabase();
    const { data } = await supa.from('jobs').select('views_count').eq('id', jobId).maybeSingle();
    if (!data) return;
    await supa.from('jobs').update({ views_count: (data.views_count ?? 0) + 1 }).eq('id', jobId);
  },

  /** Soft-archive — DB enum has no 'archived' state, so we use 'closed' as
   *  the archive semantic (matches the schema). */
  async archive(id: string): Promise<void> {
    await jobService.update(id, { status: 'closed' });
  },
};
